import React, { useState } from 'react'
import AddAdjusterModal from '../IncidentModals/AddAdjusterModal'
import { Row, Col, Alert, Button, UncontrolledPopover, PopoverBody, PopoverHeader, Label, UncontrolledTooltip } from 'reactstrap'
import { useApolloClient, gql, useQuery } from '@apollo/client'
import CustomYupInputForm from '../../Material/CustomYupInputForm'
import { SubrogationClaimSchema } from '../IncidentModals/IncidentModalsSchema'
import getFormattedMetadata from "../../Functions/getFormattedMetadata"
import UpdateSubroClaimInfo from '../Shared/UpdateSubroClaimInfo'
import RepAvatar from '../../Material/RepAvatar'
import './ClaimsTab.css'

const SUBROGATION_STATUS_CHOICES = gql`
  query SubrogationStatusChoices {
    subrogationStatusChoices: __type(name: "IncidentsSubrogationClaimStatusChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const UPDATE_SUBROGATION_CLAIM = gql`
  mutation updateSubrogationClaim($input: UpdateSubrogationClaimMutationInput!) {
    updateSubrogationClaim(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const ALL_TPAS = gql`
  query AllTpas($incidentId: ID!) {
    allTpas (incidentId: $incidentId, claimType: "SUBROGATION") {
      edges {
        node {
          id
          name
          tpaadjusterSet {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`

const ALL_INCIDENT_PARTIES = gql`
  query AllIncidentParties($incidentId: ID!) {
    allIncidentParties(incidentId: $incidentId) {
      edges {
        node {
          id
          partyName
          isDefault
        }
      }
    }
  }
`

const SubrogationClaimInfo = ({ claim, refetch, incidentId }) => {
    const apolloClient = useApolloClient()
    const { data: statusChoicesData } = useQuery(SUBROGATION_STATUS_CHOICES)
    const { data: tpaData, refetch: refetchTpas } = useQuery(ALL_TPAS, {
        variables: { incidentId }
    })
    const { data: partiesData } = useQuery(ALL_INCIDENT_PARTIES, {
        variables: { incidentId }
    })
    const statusOptions = statusChoicesData?.subrogationStatusChoices?.enumValues?.map(opt => ({
        label: opt.description || opt.name,
        value: opt.name
    })) || []
    const tpaOptions = tpaData?.allTpas?.edges?.map(edge => ({
        label: edge.node.name,
        value: edge.node.id,
        adjusters: edge.node.tpaadjusterSet.edges.map(adjusterEdge => ({
            label: adjusterEdge.node.name,
            value: adjusterEdge.node.id,
        })),
    })) || []
    const selectedTpaOption = claim?.tpa?.id ? { label: claim.tpa.name || "Current TPA", value: claim.tpa.id } : null
    const partyOptions = partiesData?.allIncidentParties?.edges?.map(edge => edge.node)?.filter(party => !party.isDefault)?.map(party => ({
        label: party.partyName || "Unnamed Party",
        value: party.id,
    })) || []

    const [editField, setEditField] = useState({ claimId: null, field: null })
    const [inputValues, setInputValues] = useState({})
    const [fieldLoading, setFieldLoading] = useState({})
    const [originalValues, setOriginalValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [error, setError] = useState(null)
    const [openAddAdjusterModal, setOpenAddAdjusterModal] = useState(false)
    const [openAssignedToModal, setOpenAssignedToModal] = useState(false)
    const dateValidationValues = {
        filedDate: inputValues?.filedDate ?? claim?.filedDate ?? null,
        acknowledgmentDate: inputValues?.acknowledgmentDate ?? claim?.acknowledgmentDate ?? null,
        paymentDate: inputValues?.paymentDate ?? claim?.paymentDate ?? null,
    }
    const assignedToEdges = claim?.assignedTo?.edges || []
    const claimDomId = String(claim?.pk || claim?.id || 'unknown').replace(/[^A-Za-z0-9_-]/g, '')

    const editFieldHandler = (fieldName, fieldValue = "", claimId = null) => {
        const hasActiveEdit = editField?.field && editField?.claimId === claimId
        if (hasActiveEdit && editField.field !== fieldName) {
            return "Please complete the edit on the current field before editing another field."
        }

        setEditField({ claimId, field: fieldName })
        setInputValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setOriginalValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setError(null)
        return ""
    }
    const saveFieldHandler = (subrogationClaimId, fieldName, fieldValue) => {
        if (fieldErrors[fieldName]) return
        if (fieldName !== "paymentDate" && (fieldValue === undefined || fieldValue === null || fieldValue === "")) {
            cancelEditHandler()
            return
        }
        updateSubrogationClaimHandler(subrogationClaimId, fieldName, fieldName === "paymentDate" && !fieldValue ? null : fieldValue)
    }
    const updateInputHandler = (e) => {
        const rawValue = e.target.value
        const fieldName = e.target.name
        const fieldType = e.target.type
        let value = rawValue
        if (["daysDown"].includes(fieldName)) {
            value = rawValue === '' ? '' : parseInt(rawValue, 10)
            if (isNaN(value)) value = ''
        }
        setInputValues(prev => ({
            ...prev,
            [fieldName]: fieldType === "select" ? rawValue === "true" ? true : rawValue === "false" ? false : rawValue : value
        }))
    }
    const cancelEditHandler = () => {
        setEditField({ claimId: null, field: null })
        setInputValues({})
        setOriginalValues({})
        setFieldErrors({})
        setError("")
        setFieldLoading({})
    }
    const updateSubrogationClaimHandler = async (subrogationClaimId, name, value) => {
        try {
            const trimmedValue = typeof value === 'string' ? value.trim() : value
            if (trimmedValue === originalValues[name]) {
                cancelEditHandler()
                return
            }
            const isClearingNullableField = name === "paymentDate" && trimmedValue === null
            const isValidValue = isClearingNullableField || typeof trimmedValue === "boolean" || typeof trimmedValue === "number" || Boolean(trimmedValue)
            if (!subrogationClaimId || !name || !isValidValue) return
            setFieldLoading(prev => ({ ...prev, [name]: true }))
            setError("")
            const input = {
                claimId: subrogationClaimId,
                [name]: trimmedValue
            }
            try {
                const result = await apolloClient.mutate({
                    mutation: UPDATE_SUBROGATION_CLAIM,
                    variables: { input },
                })
                const { ok, errors } = result?.data?.updateSubrogationClaim || {}
                if (ok) {
                    setEditField({ claimId: null, field: null })
                    setError("")
                    setInputValues(prev => ({ ...prev, [name]: trimmedValue }))
                    setOriginalValues(prev => ({ ...prev, [name]: trimmedValue }))
                    refetch()
                } else {
                    const errorMessage = errors?.[0]?.messages?.toString() ||
                        "Something went wrong, Please contact admin for support!"
                    setError(errorMessage)
                }
            } catch (err) {
                setError(err.message)
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setEditField({ claimId: null, field: null })
            setFieldLoading(prev => ({ ...prev, [name]: false }))
            setFieldErrors(prev => ({ ...prev, [name]: "" }))
        }
    }

    const getAdjusterOptions = () => {
        const selectedTpa = tpaOptions.find(opt => opt.value === (inputValues?.tpaId || claim?.tpa?.id))
        return selectedTpa?.adjusters || []
    }

    const handleAddAdjuster = async (newAdjuster) => {
        refetchTpas()
        setInputValues(prev => ({
            ...prev,
            adjusterId: newAdjuster.id
        }))
        setOpenAddAdjusterModal(false)
    }

    const booleanOptions = [{ value: true, label: "Yes" }, { value: false, label: "No" }]
    const decimalInputProps = { step: "any", inputMode: "decimal" }
    const estimatedPd = claim?.estimatedPd
    const estimatedLou = claim?.estimatedLou
    const estimatedDv = claim?.estimatedDv
    const estimatedTs = claim?.estimatedTs
    const showRecoveredPd = estimatedPd > 0 || claim?.recoveredPd > 0
    const showRecoveredLou = estimatedLou > 0 || claim?.recoveredLou > 0
    const showRecoveredDv = estimatedDv > 0 || claim?.recoveredDv > 0
    const showRecoveredTs = estimatedTs > 0 || claim?.recoveredTs > 0
    const showRecoveredAmountFields = [showRecoveredPd, showRecoveredLou, showRecoveredDv, showRecoveredTs].some(Boolean)



    let formattedMetadata = getFormattedMetadata(claim.metadata)
    let hasMetadata = formattedMetadata && formattedMetadata !== '{}' && formattedMetadata !== '[]'

    return (
        <Row>
            {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Party"
                    fieldName={"partyId"}
                    inputType="select"
                    options={partyOptions}
                    showEdit={!claim?.isArchived && !claim?.party?.id}
                    isEditing={editField.claimId === claim.id && editField.field === "partyId"}
                    isLoading={!!fieldLoading?.partyId}
                    inputValue={inputValues?.["partyId"]}
                    defaultValue={claim?.party?.id}
                    onEdit={() => editFieldHandler("partyId", claim.party?.id, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.partyId}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Status"
                    fieldName={"status"}
                    inputType="select"
                    options={statusOptions}
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "status"}
                    isLoading={!!fieldLoading?.status}
                    inputValue={inputValues?.["status"]}
                    defaultValue={claim?.status}
                    onEdit={() => editFieldHandler("status", claim.status, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.status}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <Row>
                    <Col xs="9">
                        <Label>Assigned To</Label>
                    </Col>
                    <Col xs="3" className="text-right">
                        {!claim?.isArchived && (
                            <i id={`subroClaimInfoAssignedToEdit${claimDomId}`} className="fa fa-lg fa-pencil-square cursor-pointer" onClick={() => setOpenAssignedToModal(true)} title="Edit" />
                        )}
                        {openAssignedToModal &&
                            <UpdateSubroClaimInfo
                                title="Edit Assigned To"
                                name="assignedToIds"
                                open={openAssignedToModal}
                                value={assignedToEdges.map(e => e.node)}
                                target={`subroClaimInfoAssignedToEdit${claimDomId}`}
                                handleClose={() => setOpenAssignedToModal(false)}
                                claimId={claim.id}
                                refetch={refetch}
                            />
                        }
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <span id={`assignedTo${claimDomId}`}>
                            {assignedToEdges?.slice(0, 1).map(e => <RepAvatar key={e.node.id} rep={e.node} />)}
                            {assignedToEdges?.length > 1 ? ` + ${assignedToEdges?.length - 1} ` : ""}
                        </span>
                        {assignedToEdges?.length > 1 && <UncontrolledTooltip target={`assignedTo${claimDomId}`} placement="top" style={{ backgroundColor: "#f2f2f2" }}>
                            {assignedToEdges?.map(e => <RepAvatar key={e.node.id} rep={e.node} keepOpen />)}
                        </UncontrolledTooltip>}
                    </Col>
                </Row>
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="TPA"
                    fieldName={"tpaId"}
                    inputType="select"
                    options={tpaOptions}
                    selectedOption={selectedTpaOption}
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "tpaId"}
                    isLoading={!!fieldLoading?.tpaId}
                    inputValue={inputValues?.["tpaId"]}
                    defaultValue={claim?.tpa?.id}
                    onEdit={() => editFieldHandler("tpaId", claim.tpa?.id, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.tpaId}
                />
            </Col>
            <Col xs={3} className="mb-2 d-flex align-items-center">
                <span id={`subrogation-adjuster-info-${claim.pk || claim.id}`} className="w-100">
                    <CustomYupInputForm
                        label="Adjuster"
                        fieldName={"adjusterId"}
                        inputType="select"
                        showEdit={!claim?.isArchived}
                        options={getAdjusterOptions()}
                        isEditing={editField.claimId === claim.id && editField.field === "adjusterId"}
                        isLoading={!!fieldLoading?.adjusterId}
                        inputValue={inputValues?.["adjusterId"]}
                        defaultValue={claim?.adjuster?.id}
                        onEdit={() => editFieldHandler("adjusterId", claim.adjuster?.id, claim.id)}
                        onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                        onChange={updateInputHandler}
                        onCancel={cancelEditHandler}
                    />
                </span>
                <UncontrolledPopover trigger="hover" placement="right" target={`subrogation-adjuster-info-${claim.pk || claim.id}`}>
                    <PopoverHeader>Adjuster Info</PopoverHeader>
                    <PopoverBody>
                        <b>Name:</b> <small>{claim?.adjuster?.name || "--"} </small><br />
                        <b>Phone:</b> <small>{claim?.adjuster?.phone || "--"} </small><br />
                        <b>Email:</b> <small>{claim?.adjuster?.email || "--"} </small><br />
                        <b>Role:</b> <small>{claim?.adjuster?.role || "--"} </small><br />
                    </PopoverBody>
                </UncontrolledPopover>
                {!claim?.isArchived && editField.claimId === claim.id && editField.field === "adjusterId" &&
                    <Button color="primary" onClick={() => setOpenAddAdjusterModal(true)} className='mt-4 p-2'>
                        <i className='fa fa-plus' />
                    </Button>
                }
                {openAddAdjusterModal && (
                    <AddAdjusterModal
                        isOpen={openAddAdjusterModal}
                        toggle={() => setOpenAddAdjusterModal(false)}
                        tpaId={claim?.tpa?.id}
                        onCreate={handleAddAdjuster}
                    />
                )}
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="External ID"
                    fieldName={"externalId"}
                    inputType="text"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "externalId"}
                    isLoading={!!fieldLoading?.externalId}
                    inputValue={inputValues?.["externalId"]}
                    defaultValue={claim?.externalId}
                    onEdit={() => editFieldHandler("externalId", claim.externalId, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.externalId}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="TPA Expenses"
                    fieldName={"tpaExpenses"}
                    inputType="number"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "tpaExpenses"}
                    isLoading={!!fieldLoading?.tpaExpenses}
                    inputValue={inputValues?.["tpaExpenses"]}
                    defaultValue={claim?.tpaExpenses}
                    onEdit={() => editFieldHandler("tpaExpenses", claim.tpaExpenses, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.tpaExpenses}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Filed Date"
                    fieldName={"filedDate"}
                    inputType="date"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "filedDate"}
                    isLoading={!!fieldLoading?.filedDate}
                    inputValue={inputValues?.["filedDate"]}
                    defaultValue={claim?.filedDate}
                    onEdit={() => editFieldHandler("filedDate", claim.filedDate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema}
                    validationValues={dateValidationValues}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Acknowledgment Date"
                    fieldName={"acknowledgmentDate"}
                    inputType="date"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "acknowledgmentDate"}
                    isLoading={!!fieldLoading?.acknowledgmentDate}
                    inputValue={inputValues?.["acknowledgmentDate"]}
                    defaultValue={claim?.acknowledgmentDate}
                    onEdit={() => editFieldHandler("acknowledgmentDate", claim.acknowledgmentDate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema}
                    validationValues={dateValidationValues}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Payment ID"
                    fieldName={"paymentId"}
                    inputType="text"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "paymentId"}
                    isLoading={!!fieldLoading?.paymentId}
                    inputValue={inputValues?.["paymentId"]}
                    defaultValue={claim?.paymentId}
                    onEdit={() => editFieldHandler("paymentId", claim.paymentId, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.paymentId}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Payment Date"
                    fieldName={"paymentDate"}
                    inputType="date"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "paymentDate"}
                    isLoading={!!fieldLoading?.paymentDate}
                    inputValue={inputValues?.["paymentDate"]}
                    defaultValue={claim?.paymentDate}
                    onEdit={() => editFieldHandler("paymentDate", claim.paymentDate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.paymentDate}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="In Litigation"
                    fieldName={"inLitigation"}
                    inputType="select"
                    options={booleanOptions}
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "inLitigation"}
                    isLoading={!!fieldLoading?.inLitigation}
                    inputValue={inputValues?.["inLitigation"]}
                    defaultValue={claim?.inLitigation}
                    onEdit={() => editFieldHandler("inLitigation", claim.inLitigation, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.inLitigation}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Daily Rate"
                    fieldName={"dailyRate"}
                    inputType="number"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "dailyRate"}
                    isLoading={!!fieldLoading?.dailyRate}
                    inputValue={inputValues?.["dailyRate"]}
                    defaultValue={claim?.dailyRate}
                    onEdit={() => editFieldHandler("dailyRate", claim.dailyRate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.dailyRate}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Days Down"
                    fieldName={"daysDown"}
                    inputType="number"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "daysDown"}
                    isLoading={!!fieldLoading?.daysDown}
                    inputValue={inputValues?.["daysDown"]}
                    defaultValue={claim?.daysDown}
                    onEdit={() => editFieldHandler("daysDown", claim.daysDown, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.daysDown}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Loss Of Use"
                    fieldName={"lossOfUse"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    defaultValue={claim?.lossOfUse}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Estimated Repair"
                    fieldName={"estimatedRepair"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "estimatedRepair"}
                    isLoading={!!fieldLoading?.estimatedRepair}
                    inputValue={inputValues?.["estimatedRepair"]}
                    defaultValue={claim?.estimatedRepair}
                    onEdit={() => editFieldHandler("estimatedRepair", claim.estimatedRepair, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.estimatedRepair}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Actual Repair"
                    fieldName={"actualRepair"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "actualRepair"}
                    isLoading={!!fieldLoading?.actualRepair}
                    inputValue={inputValues?.["actualRepair"]}
                    defaultValue={claim?.actualRepair}
                    onEdit={() => editFieldHandler("actualRepair", claim.actualRepair, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.actualRepair}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="TPA Fee"
                    fieldName={"tpaFee"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    showEdit={false}
                    defaultValue={claim?.tpaFee}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="TPA Net Collected"
                    fieldName={"tpaNetCollected"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    defaultValue={claim?.tpaNetCollected}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Net Collected"
                    fieldName={"netCollected"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    defaultValue={claim?.netCollected}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Recovery Rate"
                    fieldName={"recoveryRate"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    defaultValue={claim?.recoveryRate}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomYupInputForm
                    label="Liability Percentage"
                    fieldName={"liabilityPercentage"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    defaultValue={claim?.liabilityPercentage}
                />
            </Col>
            <Col xs={12}>
                <Row>
                    <Col xs={3} className="mb-2">
                        <CustomYupInputForm
                            label="Estimated Property Damage"
                            fieldName={"estimatedPd"}
                            inputType="number"
                            inputProps={decimalInputProps}
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "estimatedPd"}
                            isLoading={!!fieldLoading?.estimatedPd}
                            inputValue={inputValues?.["estimatedPd"]}
                            defaultValue={claim?.estimatedPd}
                            onEdit={() => editFieldHandler("estimatedPd", claim.estimatedPd, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.estimatedPd}
                        />
                    </Col>
                    <Col xs={3} className="mb-2">
                        <CustomYupInputForm
                            label="Estimated Loss of Use"
                            fieldName={"estimatedLou"}
                            inputType="number"
                            inputProps={decimalInputProps}
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "estimatedLou"}
                            isLoading={!!fieldLoading?.estimatedLou}
                            inputValue={inputValues?.["estimatedLou"]}
                            defaultValue={claim?.estimatedLou}
                            onEdit={() => editFieldHandler("estimatedLou", claim.estimatedLou, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.estimatedLou}
                        />
                    </Col>
                    <Col xs={3} className="mb-2">
                        <CustomYupInputForm
                            label="Estimated Diminished Value"
                            fieldName={"estimatedDv"}
                            inputType="number"
                            inputProps={decimalInputProps}
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "estimatedDv"}
                            isLoading={!!fieldLoading?.estimatedDv}
                            inputValue={inputValues?.["estimatedDv"]}
                            defaultValue={claim?.estimatedDv}
                            onEdit={() => editFieldHandler("estimatedDv", claim.estimatedDv, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.estimatedDv}
                        />
                    </Col>
                    <Col xs={3} className="mb-2">
                        <CustomYupInputForm
                            label="Estimated Tow & Storage"
                            fieldName={"estimatedTs"}
                            inputType="number"
                            inputProps={decimalInputProps}
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "estimatedTs"}
                            isLoading={!!fieldLoading?.estimatedTs}
                            inputValue={inputValues?.["estimatedTs"]}
                            defaultValue={claim?.estimatedTs}
                            onEdit={() => editFieldHandler("estimatedTs", claim.estimatedTs, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.estimatedTs}
                        />
                    </Col>
                </Row>
            </Col>
            {showRecoveredAmountFields && (
                <Col xs={12}>
                    <Row>
                        {showRecoveredPd && (
                            <Col xs={3} className="mb-2 subro-recovered-field">
                                <CustomYupInputForm
                                    label="Recovered Property Damage"
                                    fieldName={"recoveredPd"}
                                    inputType="number"
                                    inputProps={decimalInputProps}
                                    showEdit={!claim?.isArchived}
                                    isEditing={editField.claimId === claim.id && editField.field === "recoveredPd"}
                                    isLoading={!!fieldLoading?.recoveredPd}
                                    inputValue={inputValues?.["recoveredPd"]}
                                    defaultValue={claim?.recoveredPd}
                                    onEdit={() => editFieldHandler("recoveredPd", claim.recoveredPd, claim.id)}
                                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                    onChange={updateInputHandler}
                                    onCancel={cancelEditHandler}
                                    yupSchema={SubrogationClaimSchema.fields.recoveredPd}
                                />
                            </Col>
                        )}
                        {showRecoveredLou && (
                            <Col xs={3} className="mb-2 subro-recovered-field">
                                <CustomYupInputForm
                                    label="Recovered Loss of Use"
                                    fieldName={"recoveredLou"}
                                    inputType="number"
                                    inputProps={decimalInputProps}
                                    showEdit={!claim?.isArchived}
                                    isEditing={editField.claimId === claim.id && editField.field === "recoveredLou"}
                                    isLoading={!!fieldLoading?.recoveredLou}
                                    inputValue={inputValues?.["recoveredLou"]}
                                    defaultValue={claim?.recoveredLou}
                                    onEdit={() => editFieldHandler("recoveredLou", claim.recoveredLou, claim.id)}
                                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                    onChange={updateInputHandler}
                                    onCancel={cancelEditHandler}
                                    yupSchema={SubrogationClaimSchema.fields.recoveredLou}
                                />
                            </Col>
                        )}
                        {showRecoveredDv && (
                            <Col xs={3} className="mb-2 subro-recovered-field">
                                <CustomYupInputForm
                                    label="Recovered Diminished Value"
                                    fieldName={"recoveredDv"}
                                    inputType="number"
                                    inputProps={decimalInputProps}
                                    showEdit={!claim?.isArchived}
                                    isEditing={editField.claimId === claim.id && editField.field === "recoveredDv"}
                                    isLoading={!!fieldLoading?.recoveredDv}
                                    inputValue={inputValues?.["recoveredDv"]}
                                    defaultValue={claim?.recoveredDv}
                                    onEdit={() => editFieldHandler("recoveredDv", claim.recoveredDv, claim.id)}
                                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                    onChange={updateInputHandler}
                                    onCancel={cancelEditHandler}
                                    yupSchema={SubrogationClaimSchema.fields.recoveredDv}
                                />
                            </Col>
                        )}
                        {showRecoveredTs && (
                            <Col xs={3} className="mb-2 subro-recovered-field">
                                <CustomYupInputForm
                                    label="Recovered Tow & Storage"
                                    fieldName={"recoveredTs"}
                                    inputType="number"
                                    inputProps={decimalInputProps}
                                    showEdit={!claim?.isArchived}
                                    isEditing={editField.claimId === claim.id && editField.field === "recoveredTs"}
                                    isLoading={!!fieldLoading?.recoveredTs}
                                    inputValue={inputValues?.["recoveredTs"]}
                                    defaultValue={claim?.recoveredTs}
                                    onEdit={() => editFieldHandler("recoveredTs", claim.recoveredTs, claim.id)}
                                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                    onChange={updateInputHandler}
                                    onCancel={cancelEditHandler}
                                    yupSchema={SubrogationClaimSchema.fields.recoveredTs}
                                />
                            </Col>
                        )}
                    </Row>
                </Col>
            )}
            <Col xs={6} className="mb-2">
                <CustomYupInputForm
                    label="Requested Amount"
                    fieldName={"requestedAmount"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    defaultValue={claim?.requestedAmount}
                />
            </Col>
            <Col xs={6} className="mb-2">
                <CustomYupInputForm
                    label="Gross Collected"
                    fieldName={"grossCollected"}
                    inputType="number"
                    inputProps={decimalInputProps}
                    defaultValue={claim?.grossCollected}
                />
            </Col>
            <Col xs={12} className="mb-2">
                <CustomYupInputForm
                    label="Notes"
                    fieldName={"notes"}
                    inputType="text"
                    showEdit={!claim?.isArchived}
                    isEditing={editField.claimId === claim.id && editField.field === "notes"}
                    isLoading={!!fieldLoading?.notes}
                    inputValue={inputValues?.["notes"]}
                    defaultValue={claim?.notes}
                    onEdit={() => editFieldHandler("notes", claim.notes, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.notes}
                />
            </Col>
            {hasMetadata && (
                <Col xs={12} className="mb-2">
                    <Row>

                        <Col xs="12">
                            <Label>Metadata</Label>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs="12">
                            <pre className="bg-light border rounded p-3 mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {formattedMetadata}
                            </pre>
                        </Col>
                    </Row>
                </Col>
            )}
        </Row>
    )
}

export default SubrogationClaimInfo
